import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useUser } from '../context/UserContext';
import AuthService from '../services/AuthService';
import { FaPiggyBank, FaGraduationCap, FaChartLine, FaQuestionCircle } from 'react-icons/fa';
import soundEffects from '../utils/soundEffects';
import { createRipple, createSparkle, createGlow } from '../utils/vfx';
import './WelcomeScreen.css';

const WelcomeScreen = () => {
  const navigate = useNavigate();
  const { state, actions } = useUser();
  const [guestLoading, setGuestLoading] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (AuthService.isAuthenticated() && state.user) {
      navigate('/dashboard');
    } else {
      // Play page transition sound on load
      soundEffects.playPageTransition();
    }
  }, [state.user, navigate]);

  const handleLogIn = (e) => {
    soundEffects.playClick();
    createRipple(e);
    setTimeout(() => {
      navigate('/login');
    }, 200);
  };

  const handleSignUp = (e) => {
    soundEffects.playClick();
    createRipple(e);
    setTimeout(() => {
      navigate('/signup');
    }, 200);
  };

  const handleLinkClick = (e) => {
    soundEffects.playClick();
    createRipple(e);
  };

  const handleContinueAsGuest = async (e) => {
    soundEffects.playClick();
    createRipple(e);
    setGuestLoading(true);
    try {
      const guestData = {
        name: 'Guest User',
        ageRange: '18-24',
        financialGoal: 'investing',
        learningMode: 'facts'
      };
      const response = await AuthService.guestLogin(guestData);
      actions.setUser(response.user);
      actions.setOnboarded(true);
      AuthService.saveUserData({
        user: response.user,
        isOnboarded: true,
        preferences: { learningMode: 'facts', notifications: true, dailyFactTime: '08:00' },
        progress: { level: 1, xp: 0, streak: 0, badges: [], completedFacts: [], completedLessons: [] },
        portfolio: { balance: 10000, stocks: [], transactions: [] }
      });
      toast.success("You're in! Explore the app as a guest.");
      navigate('/dashboard');
    } catch (error) {
      setGuestLoading(false);
      toast.error(error.response?.data?.error || 'Could not start guest session. Please try again.');
    }
  };

  const handleFeatureHover = (e) => {
    soundEffects.playHover();
    createSparkle(e.currentTarget, 5);
  };

  return (
    <div className="welcome-container">
      {/* Background Pattern */}
      <div className="welcome-background">
        <div className="welcome-background-circle large"></div>
        <div className="welcome-background-circle large bottom"></div>
        <div className="welcome-background-circle center"></div>
        
        {/* Mobile-friendly background elements */}
        <div className="welcome-background-circle mobile top"></div>
        <div className="welcome-background-circle mobile bottom"></div>
      </div>

      <div className="welcome-content">
        {/* Logo and Mascot */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="welcome-logo"
        >
          <div className="welcome-mascot">
            <img src="/LogoGurt.jpg" alt="InvestNYou Logo" className="welcome-logo-img" />
          </div>
          <h1 className="welcome-title">
            InvestNYou
          </h1>
          <p className="welcome-subtitle">
            Master your finances, one fact at a time
          </p>
        </motion.div>

        {/* Welcome Message */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="welcome-message"
        >
          <h2>Welcome to InvestNYou!</h2>
          <p>
            Learn to master your finances through daily facts, structured lessons, 
            and AI-powered guidance. Perfect for teens and young adults.
          </p>
        </motion.div>

        {/* Feature Icons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="welcome-features"
        >
          <div className="welcome-feature" onMouseEnter={handleFeatureHover}>
            <FaGraduationCap className="welcome-feature-icon" />
            <span className="welcome-feature-text">Daily Facts</span>
          </div>
          <div className="welcome-feature" onMouseEnter={handleFeatureHover}>
            <FaChartLine className="welcome-feature-icon" />
            <span className="welcome-feature-text">Portfolio Sim</span>
          </div>
          <div className="welcome-feature" onMouseEnter={handleFeatureHover}>
            <FaPiggyBank className="welcome-feature-icon" />
            <span className="welcome-feature-text">Smart Saving</span>
          </div>
          <div className="welcome-feature" onMouseEnter={handleFeatureHover}>
            <FaQuestionCircle className="welcome-feature-icon" />
            <span className="welcome-feature-text">AI Assistant</span>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="welcome-buttons"
        >
          <div className="welcome-button-row">
            <button
              onClick={handleSignUp}
              onMouseEnter={(e) => { soundEffects.playHover(); createGlow(e.currentTarget, '#FCD1D1', 300); }}
              className="welcome-button-secondary"
              disabled={guestLoading}
            >
              Sign Up
            </button>
            
            <button
              onClick={handleLogIn}
              onMouseEnter={(e) => { soundEffects.playHover(); createGlow(e.currentTarget, '#FCD1D1', 300); }}
              className="welcome-button-secondary"
              disabled={guestLoading}
            >
              Log In
            </button>
          </div>
          <div className="welcome-guest-row">
            <button
              type="button"
              onClick={handleContinueAsGuest}
              onMouseEnter={(e) => { soundEffects.playHover(); createGlow(e.currentTarget, 'rgba(252, 209, 209, 0.9)', 200); }}
              className="welcome-guest-button"
              disabled={guestLoading}
            >
              {guestLoading ? 'Entering...' : 'Continue as Guest'}
            </button>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="welcome-footer"
        >
          <p className="welcome-footer-text">
            Start your financial journey today
          </p>
          <div className="welcome-footer-links">
            <Link 
              to="/terms" 
              className="welcome-footer-link"
              onClick={handleLinkClick}
              onMouseEnter={() => soundEffects.playHover()}
            >
              Terms of Service
            </Link>
            <Link 
              to="/privacy" 
              className="welcome-footer-link"
              onClick={handleLinkClick}
              onMouseEnter={() => soundEffects.playHover()}
            >
              Privacy Policy
            </Link>
            <Link 
              to="/patchnotes" 
              className="welcome-footer-link"
              onClick={handleLinkClick}
              onMouseEnter={() => soundEffects.playHover()}
            >
              Patch Notes
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
